import React, { useEffect, useState } from 'react';
import { Html5Qrcode, Html5QrcodeResult } from 'html5-qrcode';
import { X, Camera } from 'lucide-react';

interface QrScannerModalProps {
  onScan: (decodedText: string) => void;
  onClose: () => void;
}

interface CameraDevice {
  id: string;
  label: string;
}

export default function QrScannerModal({ onScan, onClose }: QrScannerModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);

  // Enumerate available cameras on mount
  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (!devices || devices.length === 0) {
          setError('カメラが見つかりません。接続を確認してください。');
          return;
        }
        const cams: CameraDevice[] = devices.map((d) => ({
          id: d.id,
          label: d.label || `カメラ ${d.id}`,
        }));
        setCameras(cams);
        // Default: prefer rear/environment camera, otherwise first device
        const envCam = cams.find((c) =>
          /back|rear|environment|外側/i.test(c.label)
        );
        setSelectedCameraId((envCam ?? cams[0]).id);
      })
      .catch((err) => {
        console.error('getCameras failed:', err);
        setError('カメラへのアクセスが拒否されました。アプリのカメラ許可を確認してください。');
      });
  }, []);

  // Start/restart scanner when selectedCameraId changes
  useEffect(() => {
    if (!selectedCameraId) return;

    let html5QrCode: Html5Qrcode | null = null;
    let stopped = false;

    const startScanner = async () => {
      try {
        html5QrCode = new Html5Qrcode('qr-reader');
        setIsScanning(true);
        setError(null);

        await html5QrCode.start(
          selectedCameraId,
          {
            fps: 15,
            qrbox: { width: 250, height: 250 },
            disableFlip: false,
          },
          (decodedText: string, _result: Html5QrcodeResult) => {
            if (html5QrCode && html5QrCode.isScanning) {
              html5QrCode
                .stop()
                .then(() => {
                  setIsScanning(false);
                  onScan(decodedText);
                })
                .catch(console.error);
            }
          },
          () => {
            // Per-frame parse errors are expected — ignore
          }
        );
      } catch (err) {
        console.error('Failed to start scanner:', err);
        setIsScanning(false);
        setError(
          'カメラの起動に失敗しました。別のカメラを選択するか、権限設定を確認してください。'
        );
      }
    };

    startScanner();

    return () => {
      stopped = true;
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode.stop().catch(console.error);
      }
    };
  }, [selectedCameraId, onScan]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: '20px',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          padding: '24px',
          width: '100%',
          maxWidth: '500px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
          gap: '16px',
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'var(--color-surface-hover)',
            border: 'none',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-danger)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-muted)')}
          aria-label="閉じる"
        >
          <X size={20} />
        </button>

        <h2
          style={{
            color: 'var(--color-text)',
            margin: '0 0 4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Camera size={22} />
          QRコードをスキャン
        </h2>

        {/* Camera selector (shown when multiple cameras available) */}
        {cameras.length > 1 && (
          <select
            value={selectedCameraId}
            onChange={(e) => setSelectedCameraId(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface-hover)',
              color: 'var(--color-text)',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {cameras.map((cam) => (
              <option key={cam.id} value={cam.id}>
                {cam.label}
              </option>
            ))}
          </select>
        )}

        {/* Error message */}
        {error && (
          <div
            style={{
              color: 'var(--color-danger)',
              textAlign: 'center',
              padding: '12px',
              backgroundColor: 'rgba(255,80,80,0.08)',
              borderRadius: '8px',
              width: '100%',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        {/* QR reader viewport */}
        {!error && (
          <div style={{ width: '100%', maxWidth: '340px', margin: '0 auto' }}>
            <div id="qr-reader" style={{ width: '100%' }} />
          </div>
        )}

        <p style={{ color: 'var(--color-text-muted)', fontSize: '13px', margin: 0, textAlign: 'center' }}>
          カメラをQRコードに向けると自動で読み取ります
        </p>
      </div>
    </div>
  );
}
